import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { Translations } from "../model/messageModel";
import {
  ProcessModelFolderType,
  ProcessModelType,
  SelectedProcessModelType,
} from "model/processModel";
import { Organization } from "model/organizationModel";
import { Priority } from "hooks/usePriorities";
import { organizationsApi } from "api";
import {
  DraggableStateSnapshot,
  DraggingStyle,
  NotDraggingStyle,
} from "react-beautiful-dnd";
import { ServerTag, Task } from "model/activitiesModel";
import { SortState } from "hooks/useSortableList";
import { SupportedUserDataTypes, UserData } from "hooks/useUserDataKeys";
import { Data } from "components/dialogs/ProcessModelSchema";
import jsPDF from "jspdf";
import { LabelData } from "components/labels/label_Interface";
import { unified } from "unified";
import remarkParse from "remark-parse";

export const TASK_CARD_HEIGHT = 190;
export const LABEL_OFFSET = 20;
export const DESCRIPTION_LINE_HEIGHT = 20;
export const DESCRIPTION_OFFSET = 15;

export const doesDescriptionNeedExtraHeight = (description: string | undefined): boolean => {
  return description ? description.length > 28 : false;
};

export const getUserSchema = (
  userdataschema: string | null | undefined
): Data[] => {
  try {
    const userschemaData = JSON.parse(userdataschema || "");
    const userschema: Data[] = [];
    if (Array.isArray(userschemaData)) {
      for (const u of userschemaData) {
        if (
          Object.hasOwn(u, "id") &&
          Object.hasOwn(u, "name") &&
          Object.hasOwn(u, "type")
        ) {
          if (u.name) {
            userschema.push(u);
          }
        }
      }
    }
    return userschema || [];
  } catch {
    return [];
  }
};

export const getUserData = (
  userdata: string | null | undefined
): { [key: string]: UserData } => {
  try {
    return JSON.parse(userdata || "{}");
  } catch {
    return {};
  }
};

export const getUserDataValue = (userdata: UserData) => {
  let value = userdata.value;
  if (userdata.type === SupportedUserDataTypes.date) {
    if (dayjs(value, "DD-MM-YYYY HH:mm").isValid()) {
      value = dayjs(value, "DD-MM-YYYY HH:mm").format("DD/MM/YYYY HH:mm");
    } else {
      return ""
    }
  }
  return value;
};

interface taskDetailDialogExportParams {
  task: Task;
  selectedPriorityName?: string;
  userdataEntries: { [key: string]: UserData };
  yesLabel?: string;
  attachmentPreviews?: taskDetailDialogExportAttachmentPreview[];
  organizationName?: Organization["name"];
}

interface taskDetailDialogExportAttachmentPreview {
  name: string;
  dataUrl: string;
  mimeType: string;
}

export interface TaskDetailDialogPreviewRow {
  label: string;
  value: string;
}

export interface TaskDetailDialogPreviewData {
  documentRows: TaskDetailDialogPreviewRow[];
  userdataRows: TaskDetailDialogPreviewRow[];
  attachmentPreviews: taskDetailDialogExportAttachmentPreview[];
}

const gettaskDetailDialogExportFileName = (task: Task) => {
  const taskName = task.flownodename || task.name || task.processmodelname || "task-detail";
  return `${taskName}`.replace(/[<>:"/\\|?*]+/g, "-").trim() || "task-detail";
};

const formattaskDetailDialogExportValue = (value: unknown, yesLabel = "Si") => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "boolean") {
    return value ? yesLabel : "No";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const hastaskDetailDialogExportValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return true;
};

const markdownTotaskDetailDialogText = (markdown: unknown) => {
  if (!hastaskDetailDialogExportValue(markdown)) {
    return "";
  }

  const tree = unified().use(remarkParse).parse(String(markdown));

  const nodeToText = (node: any): string => {
    if (!node) {
      return "";
    }

    switch (node.type) {
      case "text":
      case "inlineCode":
      case "code":
        return node.value || "";
      case "break":
        return "\n";
      case "image":
        return node.alt || "";
      case "paragraph":
      case "heading":
      case "blockquote":
      case "delete":
      case "emphasis":
      case "strong":
      case "link":
        return (node.children || []).map(nodeToText).join("");
      case "list":
        return (node.children || []).map(nodeToText).filter(Boolean).join("\n");
      case "listItem":
        return (node.children || []).map(nodeToText).filter(Boolean).join(" ");
      case "table":
        return (node.children || []).map(nodeToText).filter(Boolean).join("\n");
      case "tableRow":
        return (node.children || []).map(nodeToText).filter(Boolean).join(" | ");
      case "root":
        return (node.children || []).map(nodeToText).filter(Boolean).join("\n\n");
      default:
        return (node.children || []).map(nodeToText).join("");
    }
  };

  return nodeToText(tree)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n");
};

const gettaskDetailDialogImageFormat = (mimeType: string) => {
  if (mimeType === "image/png") {
    return "PNG";
  }
  if (mimeType === "image/webp") {
    return "WEBP";
  }
  return "JPEG";
};

const normalizetaskDetailDialogImageDataUrl = (dataUrl: string, mimeType: string) => {
  if (dataUrl.startsWith("data:")) {
    return dataUrl;
  }
  return `data:${mimeType};base64,${dataUrl}`;
};

export const buildTaskDetailDialogPreviewData = ({
  task,
  selectedPriorityName,
  userdataEntries,
  yesLabel,
  attachmentPreviews = [],
}: taskDetailDialogExportParams): TaskDetailDialogPreviewData => {
  const documentRows = [
    ["Modello", task.processmodelname],
    ["Processo", task.description],
    ["Attività", task.flownodename],
    ["Priorità", task.priority_name || selectedPriorityName],
    ["Descrizione", markdownTotaskDetailDialogText(task.descriptiondetail)],
  ]
    .filter(([, value]) => hastaskDetailDialogExportValue(value))
    .map(([label, value]) => ({
      label: String(label),
      value: formattaskDetailDialogExportValue(value, yesLabel),
    }));

  const userdataRows = Object.entries(userdataEntries)
    .map(([key, data]) => [key, getUserDataValue(data)] as const)
    .filter(([, value]) => hastaskDetailDialogExportValue(value))
    .map(([label, value]) => ({
      label,
      value: formattaskDetailDialogExportValue(value, yesLabel),
    }));

  return {
    documentRows,
    userdataRows,
    attachmentPreviews: attachmentPreviews.filter(
      (preview) => hastaskDetailDialogExportValue(preview.name) && hastaskDetailDialogExportValue(preview.dataUrl)
    ),
  };
};

export const taskDetailDialogExport = ({
  task,
  selectedPriorityName,
  userdataEntries,
  yesLabel,
  attachmentPreviews = [],
  organizationName,
  mode = "download",
}: taskDetailDialogExportParams & { mode?: "download" | "preview" | "blob" }): Blob | void => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableWidth = pageWidth - margin * 2;
  const labelWidth = 54;
  const valueWidth = tableWidth - labelWidth;
  const rowPadding = 3;
  const lineHeight = 4.6;
  let y = 18;

  const addDocumentHeader = () => {
    doc.setTextColor(30, 53, 95);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(organizationName || "", margin, y);

    doc.setFont("helvetica", "normal"); 
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(new Date().toLocaleString(), pageWidth - margin, y, { align: "right" });

    y += 8;
    doc.setDrawColor(190);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;
  };

  const ensureSpace = (height = 12, sectionTitle?: string) => {
    if (y + height > pageHeight - 18) {
      doc.addPage();
      y = 18;
      if (sectionTitle) {
        addTableHeader(sectionTitle);
      }
    }
  };

  const addTableHeader = (title: string) => {
    ensureSpace(14);
    doc.setFillColor(245, 247, 250);
    doc.setDrawColor(170);
    doc.rect(margin, y, tableWidth, 9, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30);
    doc.text(title, margin + rowPadding, y + 6);
    y += 9;
  };

  const addTableRow = (label: string, value: unknown, sectionTitle: string) => {
    const formattedValue = formattaskDetailDialogExportValue(value, yesLabel);
    const labelLines = doc.splitTextToSize(label, labelWidth - rowPadding * 2);
    const valueLines = doc.splitTextToSize(formattedValue, valueWidth - rowPadding * 2);
    const rowHeight = Math.max(10, Math.max(labelLines.length, valueLines.length) * lineHeight + rowPadding * 2);

    ensureSpace(rowHeight, sectionTitle);

    doc.setDrawColor(180);
    doc.rect(margin, y, labelWidth, rowHeight);
    doc.rect(margin + labelWidth, y, valueWidth, rowHeight);

    doc.setTextColor(45);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(labelLines, margin + rowPadding, y + rowPadding + 3);

    doc.setFont("helvetica", "normal");
    doc.text(valueLines, margin + labelWidth + rowPadding, y + rowPadding + 3);
    y += rowHeight;
  };

  const addOptionalTableRow = (label: string, value: unknown, sectionTitle: string) => {
    if (!hastaskDetailDialogExportValue(value)) {
      return false;
    }
    addTableRow(label, value, sectionTitle);
    return true;
  };

  const addImageRow = (preview: taskDetailDialogExportAttachmentPreview, sectionTitle: string) => {
    try {
      const imageData = normalizetaskDetailDialogImageDataUrl(preview.dataUrl, preview.mimeType);
      const imageProperties = doc.getImageProperties(imageData);
      const maxImageWidth = valueWidth - rowPadding * 2;
      const maxImageHeight = 100;
      const imageRatio = imageProperties.width / imageProperties.height;
      const imageWidth = Math.min(maxImageWidth, maxImageHeight * imageRatio);
      const imageHeight = imageWidth / imageRatio;
      const labelLines = doc.splitTextToSize(preview.name, labelWidth - rowPadding * 2);
      const rowHeight = Math.max(16, imageHeight + rowPadding * 2, labelLines.length * lineHeight + rowPadding * 2);

      ensureSpace(rowHeight, sectionTitle);

      doc.setDrawColor(180);
      doc.rect(margin, y, labelWidth, rowHeight);
      doc.rect(margin + labelWidth, y, valueWidth, rowHeight);

      doc.setTextColor(45);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(labelLines, margin + rowPadding, y + rowPadding + 3);

      doc.addImage(
        imageData,
        gettaskDetailDialogImageFormat(preview.mimeType),
        margin + labelWidth + rowPadding,
        y + rowPadding,
        imageWidth,
        imageHeight
      );

      y += rowHeight;
    } catch {
      // Ignore images that jsPDF cannot decode.
    }
  };

  doc.setProperties({ title: gettaskDetailDialogExportFileName(task) });
  addDocumentHeader();

  const documentSection = "Dati documento";
  addTableHeader(documentSection);
  addOptionalTableRow("Modello", task.processmodelname, documentSection);
  addOptionalTableRow("Processo", task.description, documentSection);
  addOptionalTableRow("Attività", task.flownodename, documentSection);
  addOptionalTableRow("Priorità", task.priority_name || selectedPriorityName, documentSection);
  addOptionalTableRow("Descrizione", markdownTotaskDetailDialogText(task.descriptiondetail), documentSection);

  const userdataRows = Object.entries(userdataEntries)
    .map(([key, data]) => [key, getUserDataValue(data)] as const)
    .filter(([, value]) => hastaskDetailDialogExportValue(value));
  if (userdataRows.length) {
    y += 8;
    const userdataSection = "Dati personalizzati";
    addTableHeader(userdataSection);
    userdataRows.forEach(([key, value]) => {
      addTableRow(key, value, userdataSection);
    });
  }

  const printablePreviews = attachmentPreviews.filter(
    (preview) => hastaskDetailDialogExportValue(preview.name) && hastaskDetailDialogExportValue(preview.dataUrl)
  );
  if (printablePreviews.length) {
    y += 8;
    const attachmentsSection = "Allegati";
    addTableHeader(attachmentsSection);
    printablePreviews.forEach((preview) => {
      addImageRow(preview, attachmentsSection);
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let index = 1; index <= pageCount; index += 1) {
    doc.setPage(index);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Pagina ${index} / ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  if (mode === "preview") {
    doc.output("dataurlnewwindow");
  } else if (mode === "blob") {
    return doc.output("blob");
  } else {
    doc.save(`${gettaskDetailDialogExportFileName(task)}.pdf`);
  }
};

export const range = (n: number) => {
  const res = [];
  for (let i = 1; i <= n; i++) {
    res.push(i);
  }
  return res;
};
export const getLabelDataFromJsonTags = (
  jsonTags: any
): LabelData[] => {
  const tags = typeof jsonTags === "string" ? JSON.parse(jsonTags || "[]") : (jsonTags || []);
  return (Array.isArray(tags) ? tags : []).map(
    (tag: { idtag: number; tag: string; color: string }) => ({
      id: tag.idtag,
      name: tag.tag,
      css: tag.color,
    })
  );
};

export const trackPageView = (url: string) => {
  (window as any).gtag("config", process.env.REACT_APP_ANALYTICS_KEY, {
    page_path: url,
  });
};

export interface SortCriterion {
  [field: string]: SortState;
}

export interface FilterChipConfig {
  label: string;
  onDelete: () => void;
}

interface BuildMyActivitiesFilterChipsParams {
  searchText: string;
  showSearchChip: boolean;
  selectedModels: number[];
  processModels: ProcessModelType[];
  selectedActivities: string[];
  selectedUserDataKey: string | null;
  searchTags: number[];
  filteredTags: ServerTag[];
  selectedPriorities: string[];
  activityNoNameLabel: string;
  setSearchText: (text: string) => void;
  setShowSearchChip: (show: boolean) => void;
  setSelectedModels: (models: number[]) => void;
  setSelectedActivities: (activities: string[]) => void;
  handleSelectUserDataKey: (key: string | null) => void;
  setSearchTags: (updater: (prev: number[]) => number[]) => void;
  setSelectedPriorities: (updater: (prev: string[]) => string[]) => void;
}

export const buildMyActivitiesFilterChips = ({
  searchText,
  showSearchChip,
  selectedModels,
  processModels,
  selectedActivities,
  selectedUserDataKey,
  searchTags,
  filteredTags,
  selectedPriorities,
  activityNoNameLabel,
  setSearchText,
  setShowSearchChip,
  setSelectedModels,
  setSelectedActivities,
  handleSelectUserDataKey,
  setSearchTags,
  setSelectedPriorities,
}: BuildMyActivitiesFilterChipsParams): FilterChipConfig[] => {
  const chips: FilterChipConfig[] = [];
  const search = searchText.trim();

  if (showSearchChip && search) {
    chips.push({
      label: search,
      onDelete: () => {
        setSearchText("");
        setShowSearchChip(false);
      },
    });
  }

  selectedModels.forEach((id) => {
    const model = processModels.find((processModel) => processModel.id === id);
    if (model) {
      chips.push({
        label: model.name,
        onDelete: () =>
          setSelectedModels(selectedModels.filter((modelId) => modelId !== id)),
      });
    }
  });

  selectedActivities.forEach((activity) => {
    chips.push({
      label: activity || activityNoNameLabel,
      onDelete: () =>
        setSelectedActivities(selectedActivities.filter((item) => item !== activity)),
    });
  });

  if (selectedUserDataKey) {
    chips.push({
      label: selectedUserDataKey,
      onDelete: () => handleSelectUserDataKey(null),
    });
  }

  searchTags.forEach((tagId) => {
    const tag = filteredTags.find((item) => item.idtag === tagId);
    chips.push({
      label: tag?.tag || String(tagId),
      onDelete: () => setSearchTags((prev) => prev.filter((id) => id !== tagId)),
    });
  });

  selectedPriorities.forEach((priority) => {
    chips.push({
      label: priority,
      onDelete: () =>
        setSelectedPriorities((prev) => prev.filter((item) => item !== priority)),
    });
  });

  return chips;
};

export const filterTasks =
  (
    filter: string,
    idtags: number[], //array degli ID tag per filtrare i task
    selectedPriorities: string[], //array delle priorities
    selectedActivities: string[],
    userDataKey: string | null,
    processModelsId: number[],
    isInAnd: boolean = true
  ) =>
    (task: Task) => {
      //
      const matchesSearchFilter =
        task.processmodelname.toLowerCase().includes(filter.toLowerCase()) ||
        task.flownodename?.toLowerCase().includes(filter.toLowerCase()) ||
        task.description.toLowerCase().includes(filter.toLowerCase()) ||
        task.description_detail?.toLowerCase().includes(filter.toLowerCase());
      //
      const tags = JSON.parse(task.jsontag || "[]") as ServerTag[];
      const matchesTags =
        idtags.length === 0 || tags.some((tag) => idtags.includes(tag.idtag));
      //
      const matchesPriority =
        selectedPriorities.length === 0 ||
        selectedPriorities.includes(task.priority_name);
      //
      const userData = getUserData(task.userdata);
      const matchesUserData = !userDataKey || userDataKey in userData;
      //
      const matchesProcessModelName =
        processModelsId.length === 0 ||
        processModelsId.includes(task.idprocessmodel);
      const result = isInAnd
        ? matchesSearchFilter &&
        matchesTags &&
        matchesPriority &&
        matchesUserData &&
        matchesProcessModelName
        : matchesSearchFilter ||
        matchesTags ||
        matchesPriority ||
        matchesUserData ||
        matchesProcessModelName;
      //
      return result;
    };

//GENERATA DA CHATGPT
const extractNumber = (item: number | string) => {
  if (typeof item === "number") {
    return item;
  }
  const match = item?.match(/\d+/);
  return match ? parseInt(match[0], 10) : Infinity; // Se non c'è numero, ritorna Infinity
};

//GENERATA DA CHATGPT
export const customSort = (
  a: number | string,
  b: number | string,
  userDataSortType: SupportedUserDataTypes
) => {
  if (userDataSortType === SupportedUserDataTypes.number) {
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    } else if (!isNaN(numA)) {
      return -1;
    } else {
      return 1;
    }
  } else if (userDataSortType === SupportedUserDataTypes.date) {
    const format = "DD-MM-YYYY HH:mm"; //in un pannello sortava bene e nell'altro al contrario, allora ho provato a forzare un parsing esplicito e ha funzionato, non so se è la mejo cosa
    const dateA = dayjs(a, format);
    const dateB = dayjs(b, format);

    const aValid = dateA.isValid();
    const bValid = dateB.isValid();

    if (aValid && bValid) {
      return dateA.diff(dateB);
    } else if (aValid) {
      return -1;
    } else {
      return 1;
    }
  } else {
    const numA = extractNumber(a);
    const numB = extractNumber(b);

    if (numA !== numB) {
      return numA - numB;
    }
    // Se i numeri sono uguali, ordina come stringhe
    if (typeof a === "string" && typeof b === "string") {
      return a.localeCompare(b);
    } else if (typeof a === "number") {
      return -1;
    } else {
      return 1;
    }
  }
};

export const hasDuplicates = (items: unknown[]) => {
  return new Set(items).size !== items.length;
};

export const checkUserDataSortType = (
  userdatas: Array<string | null>,
  sort: SortState,
  selectedUserDataKey: string | null
) => {
  let userDataSortType = SupportedUserDataTypes.string;
  if (selectedUserDataKey && ![undefined, SortState.None].includes(sort)) {
    const counters: Partial<Record<SupportedUserDataTypes, number>> = {};
    for (const userdata of userdatas) {
      const value = getUserData(userdata)[selectedUserDataKey];
      const countersKey = Object.keys(counters).map(
        (v) => Number(v) as SupportedUserDataTypes
      );
      if (!countersKey.includes(value?.type || SupportedUserDataTypes.string)) {
        counters[value.type || SupportedUserDataTypes.string] = 1;
      } else {
        //@ts-ignore
        counters[value.type || SupportedUserDataTypes.string] += 1;
      }
    }
    // per tutti i tipi che sono stati contati almeno una volta
    const countersKey = Object.keys(counters).map(
      (v) => Number(v) as SupportedUserDataTypes
    );
    if (Object.keys(counters).length > 0) {
      for (const k of countersKey) {
        if (!counters[userDataSortType]) {
          userDataSortType = k;
        } else if (!counters[k]) {
          //@ts-ignore
        } else if (counters[userDataSortType] < counters[k]) {
          userDataSortType = k;
        }
      }
    }
  }
  return userDataSortType;
};
export const sortTasks = (
  tasks: Task[], //array di task da ordinare
  sortCriteria: SortCriterion,
  selectedUserDataKey: string | null,
  priorities: Priority[]
): Task[] => {
  if (
    Object.values(sortCriteria).filter((crit) => crit !== SortState.None)
      .length === 0
  )
    return tasks; //task non ordinati se non ci sono criteri
  const indexedPriority = Object.fromEntries(
    priorities.map(({ idorder, name }) => [name, idorder])
  );

  const userDataSortType = checkUserDataSortType(
    tasks.map((a) => a.userdata),
    sortCriteria.userdata,
    selectedUserDataKey
  );

  return tasks.sort((a, b) => {
    //metodo sort
    for (const [field, value] of Object.entries(sortCriteria)) {
      //le entries che torna obj.entries son un array di tuple(array di array con chiave - valore)?
      let result = 0;
      if (value === SortState.None) {
        continue;
      }
      switch (field) {
        case "name":
          result = (a.description || "").localeCompare(b.description || "");
          break;
        case "startDate":
          const dateA = new Date(a.created || 0); //0 in caso non sia definito
          const dateB = new Date(b.created || 0);
          result = dateA.getTime() - dateB.getTime();
          break;
        case "priority":
          result =
            (indexedPriority[a.priority_name] || 0) -
            (indexedPriority[b.priority_name] || 0);
          break;
        case "userdata":
          //quando cambia il filtro per userdatakey il sort per userdatakey va tolto
          result = customSort(
            getUserData(a.userdata)[selectedUserDataKey || ""]?.value,
            getUserData(b.userdata)[selectedUserDataKey || ""]?.value,
            userDataSortType
          );
          break;
        default:
          continue; // salta il criterio se non riconosciuto
      }
      if (result !== 0) return value === SortState.Desc ? -result : result; // se il è risultato diverso da zero (i due elementi sono diversi), il confronto viene
      //invertito se l'ordine è 'desc', altrimenti no.
    }
    return 0;
  });
};

export const processTasks = (
  //combiniamo filter e sort
  tasks: Task[], //array di task da processare.
  filter: string,
  searchTags: number[],
  selectedPriorities: string[],
  selectedActivities: string[],
  userDataKey: string | null,
  processModelsId: number[],
  sortCriteria: SortCriterion,
  priorities: Priority[]
): Task[] => {
  const filtered = tasks.filter(
    filterTasks(
      filter,
      searchTags,
      selectedPriorities,
      selectedActivities,
      userDataKey,
      processModelsId
    )
  ); //filtriamo i task
  const sorted = sortTasks(filtered, sortCriteria, userDataKey, priorities); //ritorniamo ordinati i task filtrati
  return sorted;
};

export function getStyle(
  style: DraggingStyle | NotDraggingStyle | undefined,
  snapshot: DraggableStateSnapshot,
  isDoing: boolean
) {
  if (!isDoing || snapshot.draggingOver !== "done" || !snapshot.dropAnimation) {
    return style;
  }
  const { moveTo, curve, duration } = snapshot.dropAnimation;
  // move to the right spot
  const translate = `translate(${moveTo.x}px, ${moveTo.y}px)`;
  // add a bit of turn for fun
  const rotate = "scale(0.1)";

  // patching the existing style
  return {
    ...style,
    transform: `${translate} ${rotate}`,
    // slowing down the drop because we can
    transition: `all ${curve} ${duration + 1}s`,
  };
}
export namespace CONSTANTS {
  export const STORE_LABEL_COMPLETE_MODE = "label_complete_mode";
  export const STORE_EXTENSIONS_ELEMENTS = "extensionElements";
  export const STORE_OPP_POSITION = "oppPositions";
  export const STORE_MINIMAP_OPEN = "isMinimapOpen";
  export const STORE_OVERLAP_ENABLED = "isOverLapEnabled";
  export const STORE_SELECTED_ORGANIZATION_ID = "selected_organization_id";
  export const STORE_USER = "flowenti/user";
  export const STORE_JWT = "flowenti/jwt";
  export const STORE_SIDEBAR_OPEN = "isSidebarOpen";
  export const STORE_AUTOSAVE_ALERTS = 'autosave_alerts';
  export const RELEASE_NOTES_LAST_SEEN = "release_notes_last_seen_version";
}
export const url =
  ["test", "development"].includes(process.env.REACT_APP_ENV || "")
    ? process.env.REACT_APP_BACKEND_BASE_URI
    : "/flowentisysman/v1/";
export const REFRESH_ACTIVITIES_SECONDS = 10;
export const formatDate = (date: string | null) => {
  if (!date) {
    return "";
  }
  const timeZone = dayjs.tz.guess();
  const day = dayjs.utc(date);
  return day.tz(timeZone).format("DD/MM/YYYY HH:mm");
};
export const getTranslation = (translations: Translations, key: string) =>
  translations[key] || key;
// translations.filter((ele: MessageModel) => ele.iddescribe === key)[0]
//   ?.message ?? "";

export const create_UUID = () => {
  var dt = new Date().getTime();
  var uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      var r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      // eslint-disable-next-line no-mixed-operators
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
};
export interface OutletType {
  firstFilter: string;
  //# discourage use of any
  // setFirstFilter: any;
  setFirstFilter: Function;
  secondFilter: string;
  //# discourage use of any
  // setSecondFilter: any;
  setSecondFilter: Function;
}
export const toDataUrl = (url: string) =>
  new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
      var reader = new FileReader();
      reader.onloadend = function () {
        resolve(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.send();
  });

export interface JWTData {
  iss: string;
  exp: number;
  nbf: number;
  iat: number;
  userid: string;
  roles: string;
  ServerName: string;
  "last-event-id": string;
  username: string;
}
type ValueOf = string | symbol | number;
export type Res<T> = {
  [key: ValueOf]: T[];
};
export const groupBy = <T>(xs: T[], key: keyof T): Res<T> => {
  return xs.reduce((rv: Res<T>, x: T) => {
    (rv[x[key] as ValueOf] = rv[x[key] as ValueOf] || []).push(x);
    return rv;
  }, {});
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
export const MONTHS: Array<{ name: string; value: number }> = [
  // { name: "all", value: -1 },
  { name: "january", value: 1 },
  { name: "february", value: 2 },
  { name: "march", value: 3 },
  { name: "april", value: 4 },
  { name: "may", value: 5 },
  { name: "june", value: 6 },
  { name: "july", value: 7 },
  { name: "august", value: 8 },
  { name: "september", value: 9 },
  { name: "october", value: 10 },
  { name: "november", value: 11 },
  { name: "december", value: 12 },
];
export const DAYS: Array<{ name: string; value: number }> = [
  { name: "monday", value: 1 },
  { name: "tuesday", value: 2 },
  { name: "wednesday", value: 3 },
  { name: "thursday", value: 4 },
  { name: "friday", value: 5 },
  { name: "saturday", value: 6 },
  { name: "sunday", value: 0 },
];
export enum EventTypes {
  START,
  END,
  TIMER_START,
}
export enum TaskStates {
  TO_DO,
  DOING,
  DONE,
}

export const TASK_STATE_TO_STR = {
  [TaskStates.TO_DO]: "todo",
  [TaskStates.DOING]: "doing",
  [TaskStates.DONE]: "done",
};

export const SUPPORTED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "svg",
  "ico",
  "gif",
  "apng",
  "bmp",
  "avif",
  "pptx",
  "ppt",
  "docx",
  "xlsx",
  "xls",
  "ods",
  "odt",
  "pdf",
  "md",
  "txt",
  "csv",
  "json",
  "xml",
  "yaml",
  "yml",
  "log",
  "ts",
  "tsx",
  "js",
  "jsx",
  "html",
  "css",
  "java",
  "mp4",
  "webm",
  "mov",
  "avi",
  "mkv",
  "mp3",
  "wav",
  "ogg",
  "m4a",
  "flac"
];

export const getFileExtension = (filename: string) =>
  filename.split(".").pop()?.toLowerCase();
export const getQueryParams = (object: Record<string, any>) =>
  Object.keys(object)
    .map((key) => key + "=" + object[key])
    .join("&");

export const createPath = (
  fp: ProcessModelFolderType | SelectedProcessModelType,
  plain: (ProcessModelFolderType | SelectedProcessModelType)[]
) => {
  let results: number[] = [];
  if (fp.idfolder) {
    const result = plain.find((_) => _.id === fp.idfolder);
    if (result) {
      results.push(result.id);
      results = results.concat(createPath(result, plain));
    }
  }
  return results;
};

export const emptyImageonDragEvent = <T>(e: React.DragEvent<T>) => {
  var dragImgEl = document.getElementById("emptySpan");
  if (!dragImgEl) {
    dragImgEl = document.createElement("span");
    // set its style so it'll be effectively (but not technically) invisible and
    // won't change document flow
    dragImgEl.setAttribute(
      "style",
      "position: absolute; display: block; top: 0; left: 0; width: 0; height: 0;"
    );
    dragImgEl.setAttribute("id", "emptySpan");
    // add it to the document
    document.body.appendChild(dragImgEl);
  }
  e.dataTransfer.setDragImage(dragImgEl, 0, 0);
};

export const clearLanes = (xmlContent: string | null) => {
  if (xmlContent) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
    // Clear Collaboration node
    const extensionNode = xmlDoc.getElementsByTagName(
      "bpmn2:extensionElements"
    )[0];
    if (extensionNode) {
      extensionNode.remove();
    }
    // const itemToRemove: Array<ChildNode> = [];
    // collaborationNode.childNodes.forEach(childNode => {
    //   if(childNode) {
    //     itemToRemove.push(childNode)
    //   }
    // })
    // for (let item of itemToRemove) {
    //   item.remove();
    // }
    // Clear Collaboration node

    // Clear name and associationName attribute from lanes
    // const lanesNode = xmlDoc.getElementsByTagName("bpmn2:lane");
    // for(let i = 0; i < lanesNode.length; i++) {
    //   lanesNode[i].removeAttribute("name")
    //   // lanesNode[i].removeAttribute("associationName")
    // }
    const result = new XMLSerializer().serializeToString(xmlDoc);
    return result || "";
  }
  return "";
};

export const putPrioritiesOnServer = async (
  priorities: Priority[],
  organizationId: number
) => {
  const promises = [];
  const createPayloads = [];
  for (let priority of priorities) {
    if (priority.new && priority.deleted) continue;
    if (priority.new) {
      createPayloads.push({
        idorder: priority.idorder,
        name: priority.name,
      });
      // promises.push(
      //   organizationsApi.post(`/${organizationId}/priorities`, [
      //     {
      //       idorder: priority.idorder,
      //       name: priority.name,
      //     },
      //   ])
      // );
    } else if (priority.deleted) {
      promises.push(
        organizationsApi.delete(`/${organizationId}/priorities/${priority.id}`)
      );
    } else {
      promises.push(
        organizationsApi.put(`/${organizationId}/priorities/${priority.id}`, {
          ...priority,
        })
      );
    }
  }

  if (createPayloads.length > 0) {
    promises.push(
      organizationsApi.post(`/${organizationId}/priorities`, createPayloads)
    );
  }

  await Promise.all(promises);
};

export const formatDuration = (seconds: number | undefined) => {
  if (!seconds || seconds <= 0) return "";

  let remaining = Math.round(seconds);
  const years = Math.floor(remaining / (365 * 24 * 3600));
  remaining %= (365 * 24 * 3600);
  const months = Math.floor(remaining / (30 * 24 * 3600));
  remaining %= (30 * 24 * 3600);
  const days = Math.floor(remaining / (24 * 3600));
  remaining %= (24 * 3600);
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  remaining %= 60;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} Y`);
  if (months > 0) parts.push(`${months} M`);
  if (days > 0) parts.push(`${days} D`);
  if (hours > 0) parts.push(`${hours} h`);
  if (minutes > 0) parts.push(`${minutes} m`);
  if (remaining > 0 || parts.length === 0) parts.push(`${remaining} s`);

  return parts.join(", ");
};

export const validatePasswordStrength = (
  password: string,
  translations: Translations
) => {
  //regex generate con ChatGPT
  const hasMinLength = password.length >= 12;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasMinLength) {
    return getTranslation(translations, "password.error.minlength");
  }
  if (!hasUpperCase) {
    return getTranslation(translations, "password.error.uppercase");
  }
  if (!hasNumber) {
    return getTranslation(translations, "password.error.number");
  }
  if (!hasSpecialChar) {
    return getTranslation(translations, "password.error.specialchar");
  }
  return null;
};

//regex generata con ChatGPT
export const noSpecialChars = (email: string) =>
  /^[^',!§$%&*;<>]+$/.test(email);

export const getOffsetHeightForTask = (userdataLength: number) => {
  const userdataHeight = 19 * userdataLength;
  if (userdataHeight > 30) {
    return userdataHeight - 30;
  } else {
    return 0;
  }
};

export const getDefaultValueForType = (type: SupportedUserDataTypes) => {
  switch (type) {
    case SupportedUserDataTypes.string:
      return ""
    case SupportedUserDataTypes.number:
      return 0
    case SupportedUserDataTypes.date:
      return ""
    case SupportedUserDataTypes.boolean:
      return false
    default:
      return ""
  }
}

export interface Participant {
  idparticipant: number;
  firstname?: string;
  lastname?: string;
  name?: string;
  participanttype: number;
}

export const getParticipants = (jsonparticipant: string | null | undefined): Participant[] => {
  if (!jsonparticipant) {
    return [];
  }
  try {
    const participants = JSON.parse(jsonparticipant);
    return Array.isArray(participants) ? participants : [];
  } catch {
    return [];
  }
};

export const getInitials = (participant: Participant): string => {
  if (participant.firstname && participant.lastname) {
    const firstInitial = participant.firstname[0] || '';
    const lastInitial = participant.lastname[0] || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }
  if (participant.name) {
    return (participant.name[0] || '').toUpperCase();
  }
  return '';
};

export const getDisplayName = (participant: Participant): string => {
  if (participant.firstname && participant.lastname) {
    return `${participant.firstname} ${participant.lastname}`;
  }
  return participant.name || '';
};

export const getOffsetHeightForParticipants = (participants: Participant[], isDoing: boolean) => {
  if (isDoing) {
    return 0
  }
  const participantTypes = new Set(participants.map(p => p.participanttype));
  return (participantTypes.size - 1) * 33.2;
}

export const shouldTruncate = (value: string): boolean => {
  const plainText = DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });
  const hasHtml = value !== plainText;
  return plainText.length > 85 || hasHtml;
};

export const getFirstSentence = (value: string): string => {
  const plainText = DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });

  const match = plainText.match(/^.*?[.!?]\s/);
  if (match) {
    return match[0];
  }

  return plainText.substring(0, 50) + (plainText.length > 50 ? "..." : "");
};
